import React from 'react';
import { AutoMovement } from '../../../../types';
import { XIcon } from '../../../../components/icons';
import { Button } from '../../../../components/ui/Button';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { AUTO_MOVEMENT_MODE_OPTIONS } from '../autoMovement.constants';
import { useAutoMovementFormPanel } from '../hooks/useAutoMovementFormPanel';
import { useWhatsAppConnections } from '../hooks/useWhatsAppConnections';

interface AutoMovementFormPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: Omit<AutoMovement, 'id' | 'createdAt' | 'organizationId'>) => void;
    movementToEdit?: AutoMovement;
}

const AutoMovementFormPanel: React.FC<AutoMovementFormPanelProps> = ({
    isOpen,
    onClose,
    onSave,
    movementToEdit,
}) => {
    const { currentOrganization } = useOrganization();
    const { connections: whatsappConnections, loading: loadingChannels } = useWhatsAppConnections(
        currentOrganization?.id,
        isOpen,
    );

    const {
        message,
        setMessage,
        mode,
        setMode,
        channelId,
        setChannelId,
        stageId,
        setStageId,
        funnelStages,
        handleSubmit,
    } = useAutoMovementFormPanel({
        isOpen,
        movementToEdit,
        whatsappConnections,
        onSave,
    });

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 overlay-premium z-40 transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />

            <div className={`fixed top-0 right-0 panel-sidebar z-50 h-full w-full transform transition-transform duration-300 ease-in-out md:w-[500px] ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex h-full flex-col">
                    <header className="flex items-center justify-between border-b border-subtle bg-card px-6 py-4">
                        <h2 className="text-heading-sm text-default">
                            {movementToEdit ? 'Editar Movimentação' : 'Nova Movimentação'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-surface-subtle transition-colors"
                        >
                            <XIcon className="w-5 h-5 text-muted" />
                        </button>
                    </header>

                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="input-wrap">
                                <label htmlFor="auto-movement-message" className="input-label">
                                    Mensagem
                                </label>
                                <textarea
                                    id="auto-movement-message"
                                    name="auto-movement-message"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Digite o texto que acionará a movimentação..."
                                    className="input resize-none"
                                    rows={3}
                                    required
                                />
                                <p className="input-hint">
                                    Quando uma mensagem recebida corresponder a este padrão, a negociação será movida automaticamente.
                                </p>
                            </div>

                            <div className="input-wrap">
                                <label htmlFor="auto-movement-mode" className="input-label">
                                    Modo de correspondência
                                </label>
                                <select
                                    id="auto-movement-mode"
                                    value={mode}
                                    onChange={(e) => setMode(e.target.value as AutoMovement['mode'])}
                                    className="input"
                                >
                                    {AUTO_MOVEMENT_MODE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-wrap">
                                <label htmlFor="auto-movement-channel" className="input-label">
                                    Canal de atendimento
                                </label>
                                <select
                                    id="auto-movement-channel"
                                    value={channelId}
                                    onChange={(e) => setChannelId(e.target.value)}
                                    className="input"
                                    disabled={loadingChannels}
                                >
                                    <option value="">
                                        {loadingChannels ? 'Carregando canais...' : 'Todos os canais'}
                                    </option>
                                    {whatsappConnections.map((connection) => (
                                        <option key={connection.id} value={connection.id}>
                                            {connection.phone_number || connection.instance_name}
                                        </option>
                                    ))}
                                </select>
                                {whatsappConnections.length === 0 && !loadingChannels && (
                                    <p className="input-hint text-warning">
                                        Nenhum canal conectado. Conecte um canal em <strong>Configurações → Canais</strong>.
                                    </p>
                                )}
                            </div>

                            <div className="input-wrap">
                                <label htmlFor="auto-movement-stage" className="input-label">
                                    Mover para etapa
                                </label>
                                <select
                                    id="auto-movement-stage"
                                    value={stageId}
                                    onChange={(e) => setStageId(e.target.value)}
                                    className="input"
                                    required
                                >
                                    <option value="">Selecione uma etapa</option>
                                    {funnelStages.map((stage) => (
                                        <option key={stage.id} value={stage.id}>{stage.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="border-t border-subtle bg-surface-subtle px-6 py-4">
                            <div className="flex gap-3">
                                <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button type="submit" className="flex-1">
                                    {movementToEdit ? 'Salvar' : 'Criar'}
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AutoMovementFormPanel;
